import {
  Tooltip as BaseTooltip,
  Portal,
  type PortalProps,
  type TooltipRootProps,
} from "@ark-ui/react";
import clsx from "clsx";
import * as React from "react";
import { useThemeContext } from "../theme/Theme.js";

export type TooltipPositionProps = Pick<
  TooltipRootProps,
  "positioning"
>["positioning"];

interface TooltipProps extends TooltipRootProps {
  content: React.ReactNode;
  children: React.ReactElement;
  portalProps?: PortalProps;
  mode?: "dark" | "light";
  className?: string;
  classNames?: {
    arrow?: string;
    arrowTip?: string;
    content?: string;
  };
  withArrow?: boolean;
}

const Tooltip = React.forwardRef<
  React.ElementRef<typeof BaseTooltip.Trigger>,
  TooltipProps
>((props, ref) => {
  const {
    children,
    content,
    mode = "dark",
    withArrow = false,
    lazyMount = true,
    unmountOnExit = true,
    className,
    classNames,
    portalProps,
    ...rootProps
  } = props;

  const { primaryColor, secondaryColor } = useThemeContext();

  return (
    <BaseTooltip.Root
      lazyMount={lazyMount}
      unmountOnExit={unmountOnExit}
      {...rootProps}
    >
      <BaseTooltip.Trigger ref={ref} asChild>
        {children}
      </BaseTooltip.Trigger>
      <Portal {...portalProps}>
        <BaseTooltip.Positioner
          data-primary-color={primaryColor}
          data-secondary-color={secondaryColor}
          style={
            {
              zIndex: "var(--z-index-tooltip)",
              "--z-index": "var(--z-index-tooltip)",
            } as React.CSSProperties
          }
        >
          <BaseTooltip.Content
            className={clsx("ox-reset ox-TooltipContent", classNames?.content)}
            data-mode={mode}
          >
            {withArrow ? (
              <BaseTooltip.Arrow
                className={clsx("ox-reset ox-TooltipArrow", classNames?.arrow)}
              >
                <BaseTooltip.ArrowTip
                  className={clsx(
                    "ox-reset ox-TooltipArrowTip",
                    classNames?.arrowTip,
                  )}
                />
              </BaseTooltip.Arrow>
            ) : null}
            <React.Fragment>{content}</React.Fragment>
          </BaseTooltip.Content>
        </BaseTooltip.Positioner>
      </Portal>
    </BaseTooltip.Root>
  );
});

Tooltip.displayName = "Tooltip";
export { Tooltip };
export type { TooltipProps };
